/*
=====================================================
BOOST STITCH ESTIMATOR — assets/boost-stitch-estimator.js
=====================================================

PURPOSE:
  Standalone embroidery stitch-count estimator. A customer uploads a
  logo, sets the widest width (cm), picks a detail level, and the tool:
    1. estimates the embroidery stitch count (area-based),
    2. maps it to a Boost stitch band,
    3. renders an estimated-coverage pixel map of the logo,
    4. plays a short animation showing which option to select in the
       artwork picker on the product page.

  Recommendation only. No cart interaction. No external dependencies.

ENGINE (colour-aware, area-based):
  key colours      = image quantised to up to 8 colours; each can be
                     switched off (e.g. a background fill)
  on pixels        = pixels whose nearest key colour is on (alpha kept)
  bounding box     = box around the on pixels (the logo extent)
  fill ratio       = on pixels / bounding-box area
  filled area mm2  = width x height x fill ratio   (cm input x10; width
                     measures the bounding box, height from its aspect)
  baseline (40 wt) = filled area x BASELINE_DENSITY
  estimate         = baseline x detail multiplier
  band             = Standard / Large / Extra Large / manual quote

CALIBRATION — Audrey owns these numbers. Change the values in
  BOOST_SE_CONFIG, never the logic.

LOADED BY:
  sections/boost-stitch-estimator.liquid (deferred script tag).

BOOST CUSTOMISATION 2026-06-09 (v2.1.3):
  - On-upload status message aligned with step 3 wording ("set the size"
    rather than "set the widest width").
BOOST CUSTOMISATION 2026-06-09 (v2.1.2):
  - Coverage canvas height set to auto so the render keeps its aspect ratio
    when max-width:100% constrains the width on narrow/portrait viewports.
BOOST CUSTOMISATION 2026-06-08 (v2.1.1):
  - Colours step heading is always visible; only the detail (hint +
    swatches) toggles, via data-bse-colours-detail.
  - Customer-facing "logo" copy now reads "logo or artwork". Size-band
    names (out.band.name) are unchanged.
BOOST CUSTOMISATION 2026-06-08 (v2.1.0):
  - Size-first result. The recommended logo size is now the headline
    (data-bse-size); the estimated stitch count is rendered beneath as a
    justification line (data-bse-count), worded "Based on an estimated
    N stitches." The "Select <band>..." instruction is unchanged.
BOOST CUSTOMISATION 2026-06-07 (v2.0.0):
  - Colour-aware analysis. Image quantised to key colours; the customer
    can switch colours off. The estimate counts only the on colours, and
    the typed width measures the bounding box of the on pixels, not the
    whole frame.
  - Coverage map and stitch count both follow the on/off selection and
    re-estimate live as colours are toggled.
BOOST CUSTOMISATION 2026-06-03 (v1.1.0):
  - Inputs changed mm -> cm (converted to mm internally).
  - Coverage map (grey background + Boost pale red coverage) replaces
    the simulated-stitch preview.
  - Corner-based background detection for consistent transparency.
  - Result shows "Select <band>..." text instead of a pill.
  - Nested picker animation matching the live picker structure.
BOOST CUSTOMISATION 2026-06-03 (v1.0.0) — New file.
=====================================================
*/

(function () {
  'use strict';

  /* ============================================================
     BOOST CUSTOMISATION — CALIBRATION CONFIG (Audrey owns these)
     ============================================================ */
  var BOOST_SE_CONFIG = {
    // Baseline fill density at 40 wt standard thread, stitches per mm2.
    // Grid method: 2,000 stitches across a 25mm x 25mm solid square
    // (2000 / 625 = 3.2). Treated as all-in (typical underlay already
    // reflected); no separate underlay uplift is applied.
    baselineDensity: 6.625, // BOOST CALIBRATION 2026-06-08: raised from 5.3 to 6.625 (+25%) per Audrey/Ivan, whose production files ran ~70-80% of the estimate at 5.3. Intentionally biased to read at-or-over actual rather than under. Residual per-logo variance is inherent (digitising-dependent).

    // Detail-level multipliers applied to the 40 wt baseline.
    detailMultipliers: {
      standard: 1.0,  // Standard Detail   — 40 wt standard thread
      high: 1.15,     // High Detail       — 60 wt finer thread (more stitches)
      low: 0.6        // Low Detail / Fill — 30/12 wt heavier thread (fewer stitches)
    },

    // Stitch bands (upper bounds). Must match the embroidery options
    // shown in the artwork picker (snippets/boost-artwork-picker.liquid).
    bands: [
      { key: 'standard', name: 'Standard Logo', max: 10000 },
      { key: 'large', name: 'Large Logo', max: 16000 },
      { key: 'xl', name: 'Extra Large Logo', max: 20000 }
    ],

    // Above this, send to Audrey for a manual quote (no band shown).
    manualQuoteThreshold: 20000,

    // Image analysis.
    analysisMaxDim: 480,         // longest edge sampled for fill detection (px)
    alphaInkThreshold: 40,       // alpha above this counts as inked (0-255)
    transparentFractionMin: 0.02,// min share of transparent pixels to use alpha mode
    bgColourDistance: 30,        // colour distance from detected background = inked

    // Soft input bounds in mm (warnings, not hard blocks). 10mm = 1cm.
    minWidthMm: 10,
    maxWidthMm: 400,

    // Coverage map resolution (cells on the long edge).
    coverageGrid: 84,

    // BOOST CUSTOMISATION 2026-06-07 (v2.0.0): colour-picker analysis.
    maxKeyColours: 8,        // most key colours shown to the customer
    colourBucket: 24,        // channel rounding for the colour histogram
    mergeDistance: 48,       // merge key colours within this RGB distance
    minKeyShare: 0.004,      // ignore buckets below this share as key candidates
    nearWhiteLum: 232,       // luminance at/above this counts as near white
    nearWhiteChroma: 16,     // max-min channel at/below this counts as neutral
    cornerSampleFrac: 0.08   // corner patch size (fraction of the short edge)
  };

  /* ============================================================
     Helpers
     ============================================================ */
  function fmt(n) {
    try { return Math.round(n).toLocaleString('en-AU'); }
    catch (e) { return String(Math.round(n)); }
  }
  function roundTo(n, step) { return Math.round(n / step) * step; }

  // Sample average colour over a square patch starting at (x0,y0).
  function patchAvg(data, w, h, x0, y0, size) {
    var r = 0, g = 0, b = 0, n = 0, x, y;
    var xe = Math.min(w, x0 + size), ye = Math.min(h, y0 + size);
    for (y = y0; y < ye; y++) {
      for (x = x0; x < xe; x++) {
        var i = (y * w + x) * 4;
        r += data[i]; g += data[i + 1]; b += data[i + 2]; n++;
      }
    }
    n = n || 1;
    return [r / n, g / n, b / n];
  }

  function lum(r, g, b) { return 0.2126 * r + 0.7152 * g + 0.0722 * b; }
  function dist2(r, g, b, c) { var dr = r - c[0], dg = g - c[1], db = b - c[2]; return dr * dr + dg * dg + db * db; }
  function rgbHex(r, g, b) {
    return '#' + [r, g, b].map(function (v) {
      var t = Math.max(0, Math.min(255, v)).toString(16);
      return t.length < 2 ? '0' + t : t;
    }).join('');
  }

  // BOOST CUSTOMISATION 2026-06-07 (v2.0.0): colour-aware analysis.
  // The image is quantised to a small set of key colours; each pixel is
  // mapped to its nearest key colour. The customer can switch any colour
  // off (e.g. a background fill). Only the on colours are counted, and the
  // typed width measures the bounding box of the on pixels (not the whole
  // frame). Transparent pixels are always excluded.

  // Dominant key index across the four corners (the likely background).
  // Returns an index, or -1 when the corners do not agree.
  function cornerBackgroundKey(pixelKey, cw, ch, cfg) {
    var sz = Math.max(2, Math.floor(Math.min(cw, ch) * cfg.cornerSampleFrac));
    function dom(x0, y0) {
      var cnt = {}, x, y, xe = Math.min(cw, x0 + sz), ye = Math.min(ch, y0 + sz), k;
      for (y = y0; y < ye; y++) for (x = x0; x < xe; x++) {
        var ki = pixelKey[y * cw + x];
        if (ki === 255) continue;
        cnt[ki] = (cnt[ki] || 0) + 1;
      }
      var best = -1, mx = 0;
      for (k in cnt) { if (cnt[k] > mx) { mx = cnt[k]; best = +k; } }
      return best;
    }
    var corners = [dom(0, 0), dom(cw - sz, 0), dom(0, ch - sz), dom(cw - sz, ch - sz)];
    var tally = {}, k;
    corners.forEach(function (ki) { if (ki >= 0) tally[ki] = (tally[ki] || 0) + 1; });
    var bg = -1, mx = 0;
    for (k in tally) { if (tally[k] > mx) { mx = tally[k]; bg = +k; } }
    return mx >= 2 ? bg : -1;
  }

  // Returns { cw, ch, pixelKey (Uint8Array; 255 = transparent),
  //           keyColours [{ r,g,b,hex,count,on }], aspectFull (h/w) } or null.
  function analyseImage(img, cfg) {
    var w = img.naturalWidth || img.width;
    var h = img.naturalHeight || img.height;
    if (!w || !h) return null;

    var scale = Math.min(1, cfg.analysisMaxDim / Math.max(w, h));
    var cw = Math.max(1, Math.round(w * scale));
    var ch = Math.max(1, Math.round(h * scale));

    var c = document.createElement('canvas');
    c.width = cw; c.height = ch;
    var ctx = c.getContext('2d', { willReadFrequently: true });
    ctx.clearRect(0, 0, cw, ch);
    ctx.drawImage(img, 0, 0, cw, ch);

    var data;
    try { data = ctx.getImageData(0, 0, cw, ch).data; }
    catch (e) { return null; }

    // 1) Histogram of opaque pixels into coarse colour buckets.
    var n = cw * ch, i, opaque = 0, bs = cfg.colourBucket, buckets = {};
    for (i = 0; i < n; i++) {
      if (data[i * 4 + 3] < cfg.alphaInkThreshold) continue;
      opaque++;
      var r = data[i * 4], g = data[i * 4 + 1], b = data[i * 4 + 2];
      var bkey = Math.round(r / bs) * 65536 + Math.round(g / bs) * 256 + Math.round(b / bs);
      var e = buckets[bkey];
      if (e) { e.c++; e.r += r; e.g += g; e.b += b; }
      else buckets[bkey] = { c: 1, r: r, g: g, b: b };
    }
    if (!opaque) return null;

    var list = [], bk;
    for (bk in buckets) { var bb = buckets[bk]; list.push({ count: bb.c, r: bb.r / bb.c, g: bb.g / bb.c, b: bb.b / bb.c }); }
    list.sort(function (a, b) { return b.count - a.count; });

    // 2) Greedily build up to maxKeyColours, merging near-identical buckets;
    //    assign any remaining small buckets to the nearest key colour.
    var keys = [], minCount = opaque * cfg.minKeyShare, mergeSq = cfg.mergeDistance * cfg.mergeDistance, j;
    for (i = 0; i < list.length; i++) {
      var it = list[i], merged = false;
      for (j = 0; j < keys.length; j++) {
        if (dist2(it.r, it.g, it.b, [keys[j].r, keys[j].g, keys[j].b]) <= mergeSq) {
          var t = keys[j].count + it.count;
          keys[j].r = (keys[j].r * keys[j].count + it.r * it.count) / t;
          keys[j].g = (keys[j].g * keys[j].count + it.g * it.count) / t;
          keys[j].b = (keys[j].b * keys[j].count + it.b * it.count) / t;
          keys[j].count = t; merged = true; break;
        }
      }
      if (merged) continue;
      if (keys.length < cfg.maxKeyColours && (it.count >= minCount || keys.length < 2)) {
        keys.push({ r: it.r, g: it.g, b: it.b, count: it.count });
      } else if (keys.length) {
        var best = 0, bestd = Infinity, m;
        for (m = 0; m < keys.length; m++) {
          var dd = dist2(it.r, it.g, it.b, [keys[m].r, keys[m].g, keys[m].b]);
          if (dd < bestd) { bestd = dd; best = m; }
        }
        var t2 = keys[best].count + it.count;
        keys[best].r = (keys[best].r * keys[best].count + it.r * it.count) / t2;
        keys[best].g = (keys[best].g * keys[best].count + it.g * it.count) / t2;
        keys[best].b = (keys[best].b * keys[best].count + it.b * it.count) / t2;
        keys[best].count = t2;
      }
    }
    if (!keys.length) return null;

    var keyColours = keys.map(function (k) {
      var r = Math.round(k.r), g = Math.round(k.g), b = Math.round(k.b);
      return { r: r, g: g, b: b, hex: rgbHex(r, g, b), count: k.count, on: true };
    });

    // 3) Per-pixel nearest key colour (255 for transparent).
    var pixelKey = new Uint8Array(n);
    for (i = 0; i < n; i++) {
      if (data[i * 4 + 3] < cfg.alphaInkThreshold) { pixelKey[i] = 255; continue; }
      var pr = data[i * 4], pg = data[i * 4 + 1], pb = data[i * 4 + 2], bi = 0, bd = Infinity, q;
      for (q = 0; q < keyColours.length; q++) {
        var dq = dist2(pr, pg, pb, [keyColours[q].r, keyColours[q].g, keyColours[q].b]);
        if (dq < bd) { bd = dq; bi = q; }
      }
      pixelKey[i] = bi;
    }

    // 4) Auto-default: switch off the corner background and near-white colours.
    var bgKey = cornerBackgroundKey(pixelKey, cw, ch, cfg);
    for (i = 0; i < keyColours.length; i++) {
      var kc = keyColours[i];
      var nearWhite = lum(kc.r, kc.g, kc.b) >= cfg.nearWhiteLum &&
        (Math.max(kc.r, kc.g, kc.b) - Math.min(kc.r, kc.g, kc.b)) <= cfg.nearWhiteChroma;
      if (i === bgKey || nearWhite) kc.on = false;
    }
    if (!keyColours.some(function (k) { return k.on; })) {
      var idx = -1, mxc = -1;
      for (i = 0; i < keyColours.length; i++) {
        if (i !== bgKey && keyColours[i].count > mxc) { mxc = keyColours[i].count; idx = i; }
      }
      keyColours[idx < 0 ? 0 : idx].on = true;
    }

    return { cw: cw, ch: ch, pixelKey: pixelKey, keyColours: keyColours, aspectFull: h / w };
  }

  // Coverage over the on colours: bounding box of the on pixels and the
  // fill ratio inside that box. Returns { onCount: 0 } when nothing is on.
  function computeCoverage(analysis) {
    var pk = analysis.pixelKey, kc = analysis.keyColours, cw = analysis.cw, ch = analysis.ch;
    var onFlag = kc.map(function (k) { return k.on ? 1 : 0; });
    var minX = cw, minY = ch, maxX = -1, maxY = -1, onCount = 0, x, y;
    for (y = 0; y < ch; y++) for (x = 0; x < cw; x++) {
      var ki = pk[y * cw + x];
      if (ki === 255 || !onFlag[ki]) continue;
      onCount++;
      if (x < minX) minX = x; if (x > maxX) maxX = x;
      if (y < minY) minY = y; if (y > maxY) maxY = y;
    }
    if (!onCount) return { onCount: 0 };
    var bw = Math.max(1, maxX - minX + 1), bh = Math.max(1, maxY - minY + 1);
    return {
      onCount: onCount, bboxW: bw, bboxH: bh, bboxArea: bw * bh,
      fillRatioInBox: onCount / (bw * bh), aspectBox: bh / bw
    };
  }

  /* ============================================================
     Estimated-coverage map (pixels): grey background, Boost pale red
     coverage. Communicates how much of the logo is covered, without
     implying a finished embroidery look.
     ============================================================ */
  function gridDims(grid, aspect) {
    if (aspect >= 1) return [Math.max(1, Math.round(grid / aspect)), grid];
    return [grid, Math.max(1, Math.round(grid * aspect))];
  }

  function renderCoverage(analysis, canvas, cfg) {
    var pk = analysis.pixelKey, cw = analysis.cw, ch = analysis.ch;
    var onFlag = analysis.keyColours.map(function (k) { return k.on; });

    // BOOST CUSTOMISATION 2026-06-07 (v2.1.0): smooth coverage render. The mask
    // is built at the analysis resolution and scaled up with smoothing, so the
    // coverage follows the actual logo shape rather than a blocky grid. The red
    // outline is a 1px dilation of the silhouette (hugs the smooth shape and
    // matches the legend key); "not embroidered" stays a grey checker board.
    var n = cw * ch, i;
    var fillC = document.createElement('canvas'); fillC.width = cw; fillC.height = ch;
    var redC = document.createElement('canvas'); redC.width = cw; redC.height = ch;
    var fctx = fillC.getContext('2d'), rctx = redC.getContext('2d');
    var fimg = fctx.createImageData(cw, ch), rimg = rctx.createImageData(cw, ch);
    var fd = fimg.data, rd = rimg.data;
    for (i = 0; i < n; i++) {
      var ki = pk[i];
      if (ki === 255 || !onFlag[ki]) continue;
      var o = i * 4;
      fd[o] = 253; fd[o + 1] = 236; fd[o + 2] = 234; fd[o + 3] = 255; // Boost pale red #FDECEA
      rd[o] = 238; rd[o + 1] = 61;  rd[o + 2] = 36;  rd[o + 3] = 255; // Boost red #EE3D24
    }
    fctx.putImageData(fimg, 0, 0);
    rctx.putImageData(rimg, 0, 0);

    var pad = 14, target = 360;
    var scale = target / Math.max(cw, ch);
    var imgW = Math.max(1, Math.round(cw * scale)), imgH = Math.max(1, Math.round(ch * scale));
    var dpr = window.devicePixelRatio || 1;
    var cssW = imgW + pad * 2, cssH = imgH + pad * 2;
    canvas.width = Math.round(cssW * dpr);
    canvas.height = Math.round(cssH * dpr);
    canvas.style.width = cssW + 'px';
    // BOOST CUSTOMISATION 2026-06-09 (v2.1.2): height auto (not fixed px) so that when
    // max-width:100% shrinks the canvas on a narrow/portrait viewport the aspect ratio is
    // preserved, instead of the fixed height stretching the render vertically.
    canvas.style.height = 'auto';
    var ctx = canvas.getContext('2d');
    ctx.scale(dpr, dpr);

    // Grey checker board (fixed screen squares) = not embroidered.
    var greyA = '#d7d7d7', greyB = '#efefef', sq = 8, cx, cy;
    ctx.fillStyle = greyB;
    ctx.fillRect(0, 0, cssW, cssH);
    ctx.fillStyle = greyA;
    for (cy = 0; cy * sq < cssH; cy++) {
      for (cx = 0; cx * sq < cssW; cx++) {
        if ((cx + cy) % 2 === 0) ctx.fillRect(cx * sq, cy * sq, sq, sq);
      }
    }

    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';

    // Red 1px outline via an 8-direction dilation of the red silhouette, then
    // the pale red fill on top so only a thin red rim remains around (and inside)
    // the coverage.
    var dx = [-1, 1, 0, 0, -1, -1, 1, 1], dy = [0, 0, -1, 1, -1, 1, -1, 1], k;
    for (k = 0; k < dx.length; k++) {
      ctx.drawImage(redC, pad + dx[k], pad + dy[k], imgW, imgH);
    }
    ctx.drawImage(fillC, pad, pad, imgW, imgH);
  }

  /* ============================================================
     Estimate calculation
     ============================================================ */
  function bandFor(stitches, cfg) {
    if (stitches > cfg.manualQuoteThreshold) return null;
    var i;
    for (i = 0; i < cfg.bands.length; i++) { if (stitches <= cfg.bands[i].max) return cfg.bands[i]; }
    return null;
  }

  function compute(opts, cfg) {
    var cov = opts.coverage;
    var heightMm = opts.heightMode === 'manual' ? opts.heightMm : opts.widthMm * cov.aspectBox;
    var area = opts.widthMm * heightMm;
    var filledArea = area * cov.fillRatioInBox;
    var baseline = filledArea * cfg.baselineDensity;
    var mult = cfg.detailMultipliers[opts.detail] || 1.0;
    var stitches = baseline * mult;
    return { stitches: stitches, band: bandFor(stitches, cfg), heightMm: heightMm };
  }

  /* ============================================================
     Picker animation — nested replica of the live picker:
     Add logo or artwork (pale-yellow container) -> Embroidery method
     -> matching band selected.
     ============================================================ */
  function setupAnimation(root) {
    var top = root.querySelector('[data-bse-anim-toplevel]');
    var topHeader = root.querySelector('[data-bse-anim-toplevel-header]');
    var topContent = root.querySelector('[data-bse-anim-toplevel-content]');
    var method = root.querySelector('[data-bse-anim-method]');
    var methodHeader = root.querySelector('[data-bse-anim-method-header]');
    var methodContent = root.querySelector('[data-bse-anim-content]');
    var pitches = root.querySelectorAll('[data-bse-anim-pitch]');
    var cursor = root.querySelector('[data-bse-anim-cursor]');
    var stage = root.querySelector('[data-bse-anim-stage]');
    var stageMeasured = false;
    var timers = [];
    var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    function clearTimers() { timers.forEach(function (t) { clearTimeout(t); }); timers = []; }
    function at(ms, fn) { timers.push(setTimeout(fn, ms)); }

    // BOOST CUSTOMISATION 2026-06-07: reserve a fixed height for the replica so
    // the looping expand/collapse animates inside it without resizing the page.
    // Measured once from the fully-expanded toplevel; the caption and the page
    // below stay put while the accordion opens and closes.
    function measureStage() {
      if (stageMeasured || !stage || !top) return;
      var tc = topContent ? topContent.hidden : true;
      var mc = methodContent ? methodContent.hidden : true;
      if (topContent) topContent.hidden = false;
      if (methodContent) methodContent.hidden = false;
      var h = top.offsetHeight;
      if (topContent) topContent.hidden = tc;
      if (methodContent) methodContent.hidden = mc;
      if (h) { stage.style.height = h + 'px'; stageMeasured = true; }
    }

    function resetState() {
      if (top) top.classList.remove('is-active');
      if (topContent) topContent.hidden = true;
      if (method) method.classList.remove('is-open');
      if (methodContent) methodContent.hidden = true;
      pitches.forEach(function (p) { p.classList.remove('is-selected'); });
      if (cursor) cursor.classList.remove('is-visible');
    }

    function moveCursorTo(el) {
      if (!cursor || !el) return;
      var rRoot = root.getBoundingClientRect();
      var rEl = el.getBoundingClientRect();
      // BOOST CUSTOMISATION 2026-06-04 (v1.1.1): guard against a zero-size panel
      // or target rect (e.g. the result panel not yet laid out), which would
      // otherwise anchor the cursor at the far left. Skip until both have a box.
      if (!rRoot.width || !rEl.width) return;
      cursor.style.left = (rEl.left - rRoot.left + rEl.width * 0.82) + 'px';
      cursor.style.top = (rEl.top - rRoot.top + rEl.height * 0.55) + 'px';
      cursor.classList.add('is-visible');
    }

    function showEndState(targetPitch) {
      resetState();
      if (top) top.classList.add('is-active');
      if (topContent) topContent.hidden = false;
      if (method) method.classList.add('is-open');
      if (methodContent) methodContent.hidden = false;
      if (targetPitch) targetPitch.classList.add('is-selected');
    }

    function play(bandKey) {
      clearTimers();
      measureStage();
      var targetKey = bandKey || 'xl';
      var targetPitch = null;
      pitches.forEach(function (p) { if (p.getAttribute('data-bse-anim-pitch') === targetKey) targetPitch = p; });

      if (reduced) { showEndState(targetPitch); return; }

      resetState();
      at(400, function () { moveCursorTo(topHeader); });
      at(900, function () { if (top) top.classList.add('is-active'); if (topContent) topContent.hidden = false; });
      at(1700, function () { moveCursorTo(methodHeader); });
      at(2200, function () { if (method) method.classList.add('is-open'); if (methodContent) methodContent.hidden = false; });
      at(3000, function () { if (targetPitch) moveCursorTo(targetPitch); });
      at(3500, function () { if (targetPitch) targetPitch.classList.add('is-selected'); });
      at(5800, function () { if (cursor) cursor.classList.remove('is-visible'); });
      at(6600, function () { play(bandKey); });
    }

    return { play: play, stop: function () { clearTimers(); resetState(); } };
  }

  /* ============================================================
     Per-instance wiring
     ============================================================ */
  function initRoot(root) {
    var cfg = BOOST_SE_CONFIG;

    var fileInput = root.querySelector('[data-bse-file]');
    var dropZone = root.querySelector('[data-bse-drop]');
    var widthInput = root.querySelector('[data-bse-width]');
    var heightInput = root.querySelector('[data-bse-height]');
    var heightField = root.querySelector('[data-bse-height-field]');
    var estimateBtn = root.querySelector('[data-bse-estimate]');
    var resetBtn = root.querySelector('[data-bse-reset]');
    var notice = root.querySelector('[data-bse-notice]');

    var results = root.querySelector('[data-bse-results]');
    var countEl = root.querySelector('[data-bse-count]');
    var selectEl = root.querySelector('[data-bse-select]');
    var feeEl = root.querySelector('[data-bse-fee]');
    // BOOST CUSTOMISATION 2026-06-08 (v2.1.0): size-first result headline element.
    var sizeEl = root.querySelector('[data-bse-size]');
    var canvas = root.querySelector('[data-bse-canvas]');
    var animPanel = root.querySelector('[data-bse-anim]');
    var thumb = root.querySelector('[data-bse-thumb]');
    var coloursWrap = root.querySelector('[data-bse-colours]');
    // BOOST CUSTOMISATION 2026-06-08 (v2.1.1): heading stays visible; only this detail toggles.
    var coloursDetail = root.querySelector('[data-bse-colours-detail]');
    var swatchList = root.querySelector('[data-bse-swatch-list]');

    var currentImage = null;
    var currentAnalysis = null;
    var anim = animPanel ? setupAnimation(animPanel) : null;

    function setNotice(msg, kind) {
      if (!notice) return;
      notice.textContent = msg || '';
      notice.setAttribute('data-kind', kind || '');
      notice.hidden = !msg;
    }
    function heightMode() {
      var checked = root.querySelector('[name="bse-height-mode"]:checked');
      return checked ? checked.value : 'auto';
    }
    function detailValue() {
      var checked = root.querySelector('[name="bse-detail"]:checked');
      return checked ? checked.value : 'standard';
    }
    function syncHeightField() {
      if (heightField) heightField.hidden = heightMode() !== 'manual';
    }

    function clearSwatches() {
      if (swatchList) swatchList.innerHTML = '';
      // BOOST CUSTOMISATION 2026-06-08 (v2.1.1): hide only the detail; keep the step-2 heading visible.
      if (coloursDetail) coloursDetail.hidden = true;
    }

    function toggleSwatch(idx, btn) {
      if (!currentAnalysis) return;
      var kc = currentAnalysis.keyColours[idx];
      kc.on = !kc.on;
      btn.classList.toggle('is-on', kc.on);
      btn.classList.toggle('is-off', !kc.on);
      btn.setAttribute('aria-pressed', kc.on ? 'true' : 'false');
      btn.title = (kc.on ? 'Embroidered' : 'Not embroidered') + ' \u2014 ' + kc.hex;
      var st = btn.querySelector('.bse-swatch-state');
      if (st) st.textContent = kc.on ? 'On' : 'Off';
      // Live re-estimate once results are showing.
      if (results && !results.hidden) runEstimate(true);
    }

    function buildSwatches() {
      if (!swatchList || !coloursWrap) return;
      swatchList.innerHTML = '';
      // BOOST CUSTOMISATION 2026-06-08 (v2.1.1): toggle the detail wrapper, not the whole field.
      if (!currentAnalysis || !currentAnalysis.keyColours.length) { if (coloursDetail) coloursDetail.hidden = true; return; }
      currentAnalysis.keyColours.forEach(function (kc, idx) {
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'bse-swatch' + (kc.on ? ' is-on' : ' is-off');
        btn.setAttribute('data-key', idx);
        btn.setAttribute('aria-pressed', kc.on ? 'true' : 'false');
        btn.title = (kc.on ? 'Embroidered' : 'Not embroidered') + ' \u2014 ' + kc.hex;
        btn.innerHTML =
          '<span class="bse-swatch-chip" style="background:' + kc.hex + '"></span>' +
          '<span class="bse-swatch-state">' + (kc.on ? 'On' : 'Off') + '</span>';
        btn.addEventListener('click', function () { toggleSwatch(idx, btn); });
        swatchList.appendChild(btn);
      });
      if (coloursDetail) coloursDetail.hidden = false;
    }

    function loadFile(file) {
      if (!file) return;
      if (!/^image\//.test(file.type)) { setNotice('Please upload an image file (PNG, JPG or SVG). A PNG with a transparent background gives the most accurate estimate.', 'warn'); return; }
      var reader = new FileReader();
      reader.onload = function (e) {
        var img = new Image();
        img.onload = function () {
          currentImage = img;
          currentAnalysis = analyseImage(img, cfg);
          if (thumb) { thumb.src = e.target.result; thumb.hidden = false; }
          if (dropZone) dropZone.classList.add('has-file');
          if (currentAnalysis) {
            buildSwatches();
            setNotice('Logo or artwork loaded. Check the detected colours, set the size and a detail level, then click the Recommend Embroidery Size button to see our recommendation.', 'ok');
          } else {
            clearSwatches();
            setNotice('That image could not be analysed for colour. Try a PNG or JPG export of your logo or artwork.', 'warn');
          }
        };
        img.onerror = function () { setNotice('That image could not be read. Try a PNG or JPG export of your logo or artwork.', 'warn'); };
        img.src = e.target.result;
      };
      reader.onerror = function () { setNotice('That file could not be read. Try another export of your logo or artwork.', 'warn'); };
      reader.readAsDataURL(file);
    }

    function runEstimate(isLive) {
      setNotice('', '');
      if (!currentImage) { setNotice('Upload your logo or artwork first.', 'warn'); return; }

      var widthCm = parseFloat(widthInput && widthInput.value);
      if (!widthCm || widthCm <= 0) { setNotice('Enter the widest width of the logo or artwork in centimetres.', 'warn'); if (widthInput && !isLive) widthInput.focus(); return; }
      var widthMm = widthCm * 10;

      if (!currentAnalysis) { currentAnalysis = analyseImage(currentImage, cfg); if (currentAnalysis) buildSwatches(); }
      if (!currentAnalysis) { setNotice('The logo or artwork could not be analysed. Try a PNG or JPG export with a clear background.', 'warn'); return; }

      var cov = computeCoverage(currentAnalysis);
      if (!cov.onCount) { setNotice('All colours are switched off. Switch at least one colour on to estimate.', 'warn'); return; }

      var hMode = heightMode(), heightMmManual = 0;
      if (hMode === 'manual') {
        var heightCm = parseFloat(heightInput && heightInput.value);
        if (!heightCm || heightCm <= 0) { setNotice('Enter the height in centimetres, or switch to estimate height from the logo or artwork.', 'warn'); if (heightInput && !isLive) heightInput.focus(); return; }
        heightMmManual = heightCm * 10;
      }

      if (widthMm < cfg.minWidthMm) { setNotice('Most embroidery needs a width of at least 1cm. Lettering under 5mm tall does not embroider cleanly.', 'warn'); }
      else if (widthMm > cfg.maxWidthMm) { setNotice('That width is unusually large for embroidery. The estimate still runs, but our team will confirm feasibility.', 'warn'); }

      var out = compute({ coverage: cov, widthMm: widthMm, heightMode: hMode, heightMm: heightMmManual, detail: detailValue() }, cfg);

      var shown = roundTo(out.stitches, 100);
      // BOOST CUSTOMISATION 2026-06-08 (v2.1.0): size-first result.
      // Headline is the recommended logo size; the stitch count is demoted
      // to a small justification line beneath it.
      if (sizeEl) {
        sizeEl.textContent = out.band ? 'Recommended: ' + out.band.name : 'Recommended: Extra Large Logo';
      }
      if (countEl) {
        countEl.textContent = out.band
          ? 'Based on an estimated ' + fmt(shown) + ' stitches.'
          : 'Based on an estimated ' + fmt(shown) + ' stitches, above the 20,000 range.';
      }

      if (selectEl) {
        if (out.band) {
          selectEl.textContent = 'Select \u201C' + out.band.name + '\u201D when adding logo or artwork.';
        } else {
          selectEl.textContent = 'Select \u201CExtra Large Logo\u201D when adding logo or artwork. Our artwork team will confirm the final count.';
        }
      }

      if (feeEl) {
        feeEl.textContent = out.band
          // BOOST CUSTOMISATION 2026-06-07: $50 embroidery fee is per logo, not per order; $70 artwork fee is once per decorated order; neither scales with garment quantity.
          ? 'Embroidery carries a $50 setup fee per logo or artwork configuration, plus a $70 artwork setup fee that applies once to any decorated order. Neither fee scales with the number of garments.'
          : 'This logo or artwork is above the 20,000 stitch range. Our artwork team will confirm the final count and price before production.';
      }

      if (canvas && currentAnalysis) { renderCoverage(currentAnalysis, canvas, cfg); }

      if (results) results.hidden = false;
      if (anim) anim.play(out.band ? out.band.key : null);
      if (!isLive && results && results.focus) { results.setAttribute('tabindex', '-1'); results.focus(); }
    }

    function resetAll() {
      currentImage = null; currentAnalysis = null;
      if (fileInput) fileInput.value = '';
      if (thumb) { thumb.hidden = true; thumb.removeAttribute('src'); }
      if (dropZone) dropZone.classList.remove('has-file');
      clearSwatches();
      // BOOST CUSTOMISATION 2026-06-04 (v1.1.1): full reset to the start state.
      // Width and height return to their initial values, the detail and
      // height-mode radios return to their defaults, the height field is
      // re-hidden, and results and the animation are cleared. Previously the
      // width and the radios were left as the customer last set them.
      if (widthInput) widthInput.value = widthInput.defaultValue;
      if (heightInput) heightInput.value = heightInput.defaultValue;
      root.querySelectorAll('[name="bse-detail"]').forEach(function (r) { r.checked = r.defaultChecked; });
      root.querySelectorAll('[name="bse-height-mode"]').forEach(function (r) { r.checked = r.defaultChecked; });
      syncHeightField();
      if (results) results.hidden = true;
      if (anim) anim.stop();
      setNotice('', '');
    }

    if (fileInput) fileInput.addEventListener('change', function () { loadFile(fileInput.files && fileInput.files[0]); });
    if (estimateBtn) estimateBtn.addEventListener('click', runEstimate);
    if (resetBtn) resetBtn.addEventListener('click', resetAll);
    root.querySelectorAll('[name="bse-height-mode"]').forEach(function (r) { r.addEventListener('change', syncHeightField); });
    syncHeightField();

    if (dropZone) {
      ['dragenter', 'dragover'].forEach(function (ev) {
        dropZone.addEventListener(ev, function (e) { e.preventDefault(); dropZone.classList.add('is-dragover'); });
      });
      ['dragleave', 'drop'].forEach(function (ev) {
        dropZone.addEventListener(ev, function (e) { e.preventDefault(); dropZone.classList.remove('is-dragover'); });
      });
      dropZone.addEventListener('drop', function (e) {
        var dt = e.dataTransfer; if (dt && dt.files && dt.files[0]) loadFile(dt.files[0]);
      });
    }
  }

  function init() {
    document.querySelectorAll('[data-boost-stitch-estimator]').forEach(function (r) {
      if (!r.hasAttribute('data-bse-ready')) { r.setAttribute('data-bse-ready', '1'); initRoot(r); }
    });
  }

  if (document.readyState === 'loading') { document.addEventListener('DOMContentLoaded', init); }
  else { init(); }
})();
